import type { MessageKey } from '@dijitalapp/i18n';
import type { SpaceInvitePreviewDto } from '@dijitalapp/shared';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChatStore } from '../lib/chat-context';
import { useShell } from './Shell';

const TOKEN = /^[A-Za-z0-9_-]{32,64}$/;

/** `/space-join#<token>`: shows the community (name and size only) and joins it. */
export function JoinSpacePage() {
  const { i18n } = useApp();
  const { t } = i18n;
  const { hash } = useLocation();
  const navigate = useNavigate();
  const store = useChatStore();
  const { reload, selectSpace } = useShell();
  const token = hash.slice(1);
  const valid = TOKEN.test(token);
  const [preview, setPreview] = useState<SpaceInvitePreviewDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!valid) return;
    let alive = true;
    api<SpaceInvitePreviewDto>('POST', '/space-invites/preview', { token }).then(
      (p) => alive && setPreview(p),
      (err: unknown) => alive && setError(t(errorMessageKey(err))),
    );
    return () => {
      alive = false;
    };
  }, [token, valid, t]);

  const open = async (spaceId: string) => {
    await Promise.all([reload(), store?.loadConversations()]);
    selectSpace(spaceId);
    navigate('/spaces', { replace: true });
  };

  const join = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ spaceId: string }>('POST', '/space-invites/join', { token });
      await open(r.spaceId);
    } catch (err) {
      setError(t(errorMessageKey(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="content-inner content-narrow">
      <div className="card stack" style={{ maxWidth: 480, margin: '0 auto' }}>
        <h1 style={{ fontSize: 'var(--text-lg)' }}>{preview?.name ?? t('community.joinTitle')}</h1>
        {!valid && <p role="alert">{t('join.missingToken')}</p>}
        {error && (
          <p role="alert" className="alert alert-error">
            {error}
          </p>
        )}
        {valid && !preview && !error && <p className="muted">{t('app.loading')}</p>}
        {preview && (
          <>
            <p className="muted">
              {t(`spaces.type.${preview.type}` as MessageKey)} · {t('spaces.members', { count: preview.memberCount })}
            </p>
            {preview.spaceId ? (
              <button className="btn btn-primary" type="button" onClick={() => void open(preview.spaceId!)}>
                {t('community.open')}
              </button>
            ) : (
              <>
                <p className="hint">{t('community.joinPrivacyNote')}</p>
                <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void join()}>
                  {t('join.join')}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
